import { resolveMediaDisplayUrl } from '@/shared/lib/media-proxy'

/** 用户端站点品牌(来自 GET /auth/config.site) */

export interface SiteBrandView {
  site_name?: string
  tagline?: string
  logo_url?: string
  favicon_url?: string
  copyright?: string
  icp_number?: string
  icp_url?: string
  psb_number?: string
  psb_url?: string
  /** 模块页主标题品牌前缀,如「丝路」→「丝路作品广场」;nav 导航文案不受影响 */
  page_title_prefix?: string
  login_background?: LoginBackgroundView
}

export interface LoginBackgroundView {
  media_kind?: 'image' | 'video' | ''
  media_url?: string
  poster_url?: string
}

export const DEFAULT_ICP_URL = 'https://beian.miit.gov.cn/'

/** Logo / favicon 展示 URL；跨域 OSS 经 /api/v1/public/media/proxy 绕过防盗链 */
export function resolveSiteBrandMediaUrl(raw?: string | null): string {
  const url = raw?.trim()
  if (!url) return ''
  return resolveMediaDisplayUrl(url)
}

/**
 * 运营在站点配置里填写的站点名称。
 * 与租户显示名相同时视为未配置（兼容旧 API 用租户名回填空 site_name）。
 */
export function configuredSiteName(
  site: Pick<SiteBrandView, 'site_name'>,
  tenantName?: string | null,
): string {
  const name = site.site_name?.trim() ?? ''
  if (!name) return ''
  const tenant = tenantName?.trim() ?? ''
  if (tenant && name === tenant) return ''
  return name
}

export type SiteBrandMarkParts = {
  logo: string
  /** 是否渲染 logo 图 */
  showLogo: boolean
  /**
   * 是否渲染站点名称文案：
   * - 有 logo → 仅当运营填写了 site_name 时同显
   * - 无 logo → 显示（由调用方用租户名 / 应用名兜底）
   */
  showName: boolean
}

/** 顶栏 / 鉴权品牌位：logo 与站点名可单独或同时显示 */
export function siteBrandMarkParts(
  site: Pick<SiteBrandView, 'logo_url' | 'site_name'>,
  tenantName?: string | null,
): SiteBrandMarkParts {
  const logo = resolveSiteBrandMediaUrl(site.logo_url)
  const name = configuredSiteName(site, tenantName)
  const showLogo = Boolean(logo)
  const showName = showLogo ? Boolean(name) : true
  return { logo, showLogo, showName }
}

export function siteBrandInitial(name: string): string {
  const trimmed = name.trim()
  if (!trimmed) return 'S'
  return trimmed.charAt(0).toUpperCase()
}

export function hasConfiguredLoginBackground(site: SiteBrandView): boolean {
  const kind = site.login_background?.media_kind?.trim()
  const url = site.login_background?.media_url?.trim()
  return (kind === 'image' || kind === 'video') && !!url
}

/** 历史 seed / 占位租户名；仅此类名称才触发版权脏数据回退 */
const LEGACY_PLACEHOLDER_TENANT_NAMES = new Set(['default tenant', 'default'])

function isLegacyPlaceholderTenantName(name: string): boolean {
  return LEGACY_PLACEHOLDER_TENANT_NAMES.has(name.trim().toLowerCase())
}

/** 页脚展示用品牌名：运营站点名 > 组织名称 > 平台默认名 */
export function resolveFooterBrandName(input: {
  brandName?: string | null
  tenantName?: string | null
  appName: string
}): string {
  const brand = input.brandName?.trim() ?? ''
  if (brand) return brand
  const tenant = input.tenantName?.trim() ?? ''
  if (tenant) return tenant
  return input.appName.trim() || 'SilkReels'
}

/** 浏览器标签 title：站点名 > 组织名；seed 租户仅有 Logo 时仍用平台名 */
export function resolveDocumentTitle(input: {
  brandName?: string | null
  tenantName?: string | null
  appName: string
  hasLogo?: boolean
}): string {
  const brand = input.brandName?.trim() ?? ''
  if (brand) return brand
  const tenant = input.tenantName?.trim() ?? ''
  if (input.hasLogo && tenant && isLegacyPlaceholderTenantName(tenant)) {
    return input.appName.trim() || 'SilkReels'
  }
  return resolveFooterBrandName(input)
}

/**
 * 页脚版权文案：运营自定义优先；留空用默认「© 年 + 品牌名」。
 * 历史误存「© … Default Tenant」且未配站点名时，回退默认（品牌名用 appName）。
 */
export function resolveFooterCopyright(input: {
  custom?: string | null
  brandName?: string | null
  tenantName?: string | null
  appName: string
  year?: number
  /** i18n `site.footer.copyright`；失败时用中文硬兜底 */
  translateDefault: (year: number, name: string) => string
}): string {
  const year = input.year ?? new Date().getFullYear()
  const brand = input.brandName?.trim() ?? ''
  const tenant = input.tenantName?.trim() ?? ''
  const name = resolveFooterBrandName(input)
  const translated = input.translateDefault(year, name)
  const defaultCopyright =
    translated &&
    translated !== 'site.footer.copyright' &&
    translated.includes(String(year))
      ? translated
      : `© ${year} ${name}. 保留所有权利。`

  const custom = input.custom?.trim() ?? ''
  if (!custom) return defaultCopyright

  if (
    !brand &&
    tenant &&
    isLegacyPlaceholderTenantName(tenant) &&
    custom.toLowerCase().includes(tenant.toLowerCase())
  ) {
    return defaultCopyright
  }
  return custom
}
